import queryString from 'query-string';
import skos from '@openactive/skos';
import activityList from '../backupCache/activityList.json';

/**
 * @typedef {object} ApiHelperSessionsReqArgs
 * @property {string} apiKey
 * @property {string} startDateGte ISO 8601 date string. Fetch results with startDate ≥ this
 * @property {string} startDateLte ISO 8601 date string. Fetch results with startDate ≤ this
 * @property {string} location in the format "lat, lng, radius"
 * @property {string|null} [provider]
 * @property {string|null} [activity]
 * @property {string[]} [iminTag]
 * @property {string[]} [iminTagNot]
 * @property {boolean} [showAllSessions]
 * @property {boolean} [includeInProgressEvents] Iff true, the Timetable will
 *   include events that are still in-progress.
 */

class ApiHelper {
  static defaultLimit = 50;

  /**
   * @param {string} path URL (minus query params)
   * @param {{ [k: string]: string }} params Query params
   * @returns {string}
   */
  static buildUrl(path, params) {
    /** @type {string|null} */
    let url = null;
    if (typeof URL !== 'undefined' && typeof URLSearchParams !== 'undefined') {
      const urlObj = new URL(path);
      if (urlObj.searchParams instanceof Array) {
        Object.keys(params).forEach((key) => urlObj.searchParams.append(key, params[key]));
        url = urlObj.toString();
      } else {
        url = null;
      }
    }
    if (!url) {
      url = `${path}`;
      if (typeof params === 'object' && Object.keys(params).length > 0) {
        url = `${url}?${queryString.stringify(params)}`;
      }
    }
    return url;
  }

  /**
   * @param {ApiHelperSessionsReqArgs} args
   */
  static sessions({
    apiKey,
    startDateGte,
    startDateLte,
    location,
    provider = null,
    activity = null,
    iminTag = [],
    iminTagNot = [],
    showAllSessions = false,
    includeInProgressEvents = false,
  }) {
    /** @type {{[k:string]: any}} */
    const params = {
      'geo[radial]': location,
      mode: 'upcoming-sessions',
      'startDate[gte]': startDateGte,
      'startDate[lte]': startDateLte,
      limit: String(ApiHelper.defaultLimit),
      _isTimetableRequest: true,
    };
    if (provider) {
      params['integrationId'] = provider;
    }
    if (activity) {
      params['activityPrefLabel'] = activity;
    }
    if (iminTag.length > 0) {
      params['iminTag'] = iminTag;
    }
    if (iminTagNot.length > 0) {
      params['iminTag[not]'] = iminTagNot;
    }
    if (showAllSessions) {
      params['showAllSessions'] = showAllSessions;
    }
    if (includeInProgressEvents) {
      params['includeInProgress'] = String(includeInProgressEvents);
    }
    const url = this.buildUrl(`${process.env.REACT_APP_API_URL}events-api/v2/event-series`, params);
    return fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-API-KEY': apiKey,
      },
      mode: 'cors',
    })
      .then((res) => res.json())
      .catch(() => {
        return {
          item: [],
        };
      });
  }

  static _loadActivityListFromCache() {
    return new skos.ConceptScheme(activityList);
  }

  static getActivityList() {
    return fetch('https://www.openactive.io/activity-list/activity-list.jsonld', {
      method: 'GET',
      headers: {
        Accept: 'application/ld+json',
      },
    })
      .then((res) => {
        // If response status is not 2xx use the back up cached activity list (which could be out of date hence why we don't use it as the default)
        if (!res.ok) {
          return this._loadActivityListFromCache();
        }
        return res;
      })
      .then((res) => res.json())
      .then((json) => new skos.ConceptScheme(json))
      .catch(() => {
        return this._loadActivityListFromCache();
      });
  }
}

export default ApiHelper;
